import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

const RegulatoryUpdates = ({ market }) => {
  const [selectedType, setSelectedType] = useState('all');

  const regulatoryTypes = [
    { id: 'all', label: 'All Updates', icon: 'FileText' },
    { id: 'policy', label: 'Policy Changes', icon: 'ScrollText' },
    { id: 'compliance', label: 'Compliance', icon: 'Shield' },
    { id: 'taxation', label: 'Taxation', icon: 'Receipt' },
    { id: 'export', label: 'Export/Import', icon: 'Globe' }
  ];

  const regulatoryUpdates = [
    {
      id: 1,
      title: 'Revised Export Policy for Sugar - Quota Allocation Guidelines',
      type: 'export',
      authority: 'Ministry of Commerce & Industry',
      effectiveDate: '2025-01-15',
      priority: 'high',
      status: 'active',
      summary: 'New guidelines for export quota allocation based on production capacity and historical performance. Mills must register before January 31, 2025.',
      keyPoints: [
        'Export quota increased to 6.2 million metric tons',
        'Priority allocation for mills with ethanol production facilities',
        'Mandatory online registration portal active',
        'Quarterly review mechanism introduced'
      ],
      compliance: [
        'Submit production capacity certificates',
        'Register on DGFT portal within 15 days',
        'Maintain quality standards as per FSSAI',
        'Quarterly compliance reports mandatory'
      ],
      documents: [
        { name: 'Export Policy Guidelines 2025.pdf', size: '2.4 MB' },
        { name: 'Registration Form.pdf', size: '156 KB' }
      ],
      deadline: '2025-01-31',
      icon: 'Ship',
      color: 'blue'
    },
    {
      id: 2,
      title: 'GST Rate Revision for Sugar and By-products',
      type: 'taxation',
      authority: 'GST Council',
      effectiveDate: '2025-02-01',
      priority: 'high',
      status: 'upcoming',
      summary: 'Revised GST rates and input tax credit mechanisms for sugar manufacturing and trading. Changes applicable from February 1, 2025.',
      keyPoints: [
        'GST rate on white sugar remains at 5%',
        'Input tax credit enhanced for packaging materials',
        'Simplified return filing process',
        'E-way bill modifications for inter-state movement'
      ],
      compliance: [
        'Update accounting systems with new rates',
        'Recalculate input tax credit claims',
        'File revised GST returns before deadline',
        'Update invoice formats as per new guidelines'
      ],
      documents: [
        { name: 'GST Circular 2025.pdf', size: '1.8 MB' },
        { name: 'ITC Guidelines.pdf', size: '890 KB' }
      ],
      deadline: '2025-02-15',
      icon: 'Receipt',
      color: 'green'
    },
    {
      id: 3,
      title: 'Ethanol Blending Program - Mandatory Compliance Requirements',
      type: 'policy',
      authority: 'Ministry of Petroleum & Natural Gas',
      effectiveDate: '2024-12-15',
      priority: 'high',
      status: 'active',
      summary: 'Updated requirements for sugar mills participating in ethanol blending program with enhanced incentives and penalties for non-compliance.',
      keyPoints: [
        '20% ethanol blending target by 2025',
        'Fixed pricing mechanism for ethanol supply',
        'Quality specifications updated',
        'Penalty structure for delayed supplies'
      ],
      compliance: [
        'Obtain ethanol production licenses',
        'Install quality testing equipment',
        'Monthly production reports mandatory',
        'Dedicated storage facilities required'
      ],
      documents: [
        { name: 'EBP Guidelines 2025.pdf', size: '3.2 MB' },
        { name: 'Quality Standards.pdf', size: '1.1 MB' }
      ],
      deadline: '2025-01-10',
      icon: 'Droplet',
      color: 'purple'
    },
    {
      id: 4,
      title: 'Food Safety Standards Amendment - Sugar Processing',
      type: 'compliance',
      authority: 'FSSAI',
      effectiveDate: '2025-03-01',
      priority: 'medium',
      status: 'upcoming',
      summary: 'Amendment to food safety standards for sugar processing, packaging, and storage. Enhanced quality parameters and traceability requirements.',
      keyPoints: [
        'New sulphur dioxide residue limits',
        'Mandatory batch traceability system',
        'Updated packaging material standards',
        'Enhanced microbial testing protocols'
      ],
      compliance: [
        'Install traceability software systems',
        'Upgrade laboratory testing capabilities',
        'Train quality control personnel',
        'Update FSSAI license documentation'
      ],
      documents: [
        { name: 'FSSAI Amendment Notification.pdf', size: '2.7 MB' },
        { name: 'Quality Parameters Guide.pdf', size: '1.5 MB' }
      ],
      deadline: '2025-02-28',
      icon: 'Shield',
      color: 'red'
    },
    {
      id: 5,
      title: 'Sugarcane Pricing Policy - FRP Revision for 2024-25',
      type: 'policy',
      authority: 'Commission for Agricultural Costs & Prices',
      effectiveDate: '2024-10-01',
      priority: 'medium',
      status: 'active',
      summary: 'Fair and Remunerative Price (FRP) revised for sugarcane with premium incentives for early cane varieties and regional adjustments.',
      keyPoints: [
        'FRP increased to ₹340 per quintal',
        'Premium of ₹310 for early varieties',
        'Regional price adjustments approved',
        'Payment timeline strictly enforced'
      ],
      compliance: [
        'Update farmer payment systems',
        'Maintain variety-wise procurement records',
        'Submit monthly payment reports',
        'Ensure 14-day payment cycle compliance'
      ],
      documents: [
        { name: 'FRP Notification 2024-25.pdf', size: '980 KB' },
        { name: 'Payment Guidelines.pdf', size: '650 KB' }
      ],
      deadline: '2024-10-15',
      icon: 'Leaf',
      color: 'green'
    }
  ];

  const filteredUpdates = regulatoryUpdates?.filter((update) => {
    if (selectedType !== 'all' && update?.type !== selectedType) return false;
    return true;
  });

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high':
        return 'bg-red-100 text-red-700 border-red-200';
      case 'medium':
        return 'bg-orange-100 text-orange-700 border-orange-200';
      case 'low':
        return 'bg-blue-100 text-blue-700 border-blue-200';
      default:
        return 'bg-gray-100 text-gray-700 border-gray-200';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-700';
      case 'upcoming':
        return 'bg-blue-100 text-blue-700';
      case 'expired':
        return 'bg-gray-100 text-gray-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  const getIconColor = (color) => {
    switch (color) {
      case 'blue':
        return 'bg-blue-100 text-blue-600';
      case 'green':
        return 'bg-green-100 text-green-600';
      case 'purple':
        return 'bg-purple-100 text-purple-600';
      case 'red':
        return 'bg-red-100 text-red-600';
      default:
        return 'bg-gray-100 text-gray-600';
    }
  };

  return (
    <div className="space-y-6">
      {/* Type Filter */}
      <div className="flex items-center gap-3 overflow-x-auto pb-2">
        {regulatoryTypes?.map((type) => (
          <button
            key={type?.id}
            onClick={() => setSelectedType(type?.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
              selectedType === type?.id
                ? 'bg-primary text-white' :'bg-muted text-muted-foreground hover:bg-muted/80'
            }`}
          >
            <Icon name={type?.icon} size={16} />
            <span>{type?.label}</span>
          </button>
        ))}
      </div>

      {/* Regulatory Updates */}
      <div className="space-y-4">
        {filteredUpdates?.map((update) => (
          <div key={update?.id} className="bg-card border border-border rounded-lg overflow-hidden">
            <div className="p-6 space-y-4">
              {/* Header */}
              <div className="flex items-start gap-4">
                <div className={`flex-shrink-0 w-12 h-12 rounded-full flex items-center justify-center ${getIconColor(update?.color)}`}>
                  <Icon name={update?.icon} size={24} />
                </div>
                <div className="flex-1">
                  <div className="flex items-start justify-between gap-3 mb-2">
                    <h3 className="text-lg font-bold text-foreground">{update?.title}</h3>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <span className={`px-2 py-1 rounded text-xs font-medium border ${getPriorityColor(update?.priority)}`}>
                        {update?.priority?.toUpperCase()}
                      </span>
                      <span className={`px-2 py-1 rounded text-xs font-medium ${getStatusColor(update?.status)}`}>
                        {update?.status?.toUpperCase()}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-4 text-sm text-muted-foreground">
                    <div className="flex items-center gap-1">
                      <Icon name="Building" size={14} />
                      <span>{update?.authority}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Icon name="Calendar" size={14} />
                      <span>Effective: {new Date(update?.effectiveDate)?.toLocaleDateString()}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Summary */}
              <div className="bg-muted/30 rounded-lg p-4">
                <p className="text-sm text-foreground">{update?.summary}</p>
              </div>

              {/* Key Points */}
              <div>
                <h4 className="font-semibold text-foreground mb-2 flex items-center gap-2">
                  <Icon name="List" size={16} className="text-primary" />
                  Key Points
                </h4>
                <ul className="space-y-2">
                  {update?.keyPoints?.map((point, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-sm text-foreground">
                      <Icon name="ChevronRight" size={14} className="text-primary flex-shrink-0 mt-0.5" />
                      <span>{point}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Compliance Requirements */}
              <div>
                <h4 className="font-semibold text-foreground mb-2 flex items-center gap-2">
                  <Icon name="CheckCircle" size={16} className="text-primary" />
                  Compliance Requirements
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {update?.compliance?.map((req, idx) => (
                    <div key={idx} className="flex items-center gap-2 text-sm text-foreground bg-muted/30 rounded p-2">
                      <Icon name="Circle" size={8} className="text-primary flex-shrink-0" />
                      <span>{req}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Documents & Deadline */}
              <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 pt-4 border-t border-border">
                <div className="flex-1">
                  <h4 className="font-semibold text-foreground mb-2 text-sm">Available Documents</h4>
                  <div className="space-y-1">
                    {update?.documents?.map((doc, idx) => (
                      <button
                        key={idx}
                        className="flex items-center gap-2 text-sm text-primary hover:text-primary/80 transition-colors"
                      >
                        <Icon name="FileDown" size={14} />
                        <span>{doc?.name}</span>
                        <span className="text-muted-foreground">({doc?.size})</span>
                      </button>
                    ))}
                  </div>
                </div>

                <div className="flex items-center gap-4">
                  <div className="text-right">
                    <div className="text-xs text-muted-foreground mb-1">Compliance Deadline</div>
                    <div className="text-lg font-bold text-foreground">
                      {new Date(update?.deadline)?.toLocaleDateString()}
                    </div>
                  </div>
                  <button className="px-4 py-2 bg-primary text-white rounded-lg text-sm font-medium hover:bg-primary/90 transition-colors">
                    Mark as Read
                  </button>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {filteredUpdates?.length === 0 && (
        <div className="text-center py-12 bg-card border border-border rounded-lg">
          <Icon name="FileText" size={48} className="mx-auto text-muted-foreground mb-4" />
          <h3 className="text-lg font-semibold text-foreground mb-2">No Updates Found</h3>
          <p className="text-muted-foreground">
            No regulatory updates match your current filters
          </p>
        </div>
      )}
    </div>
  );
};

export default RegulatoryUpdates;